import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  Avatar,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Person,
  Search as SearchIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import { useToast } from "../components/Toast";
import { Loading } from "../components/Loading";

interface Staff {
  id: string;
  name: string;
  phoneNumber: string;
  role: string;
  status: string;
  profilePhoto: string | null;
  companyId: string;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export const roles = ["Cluster_Leader", "Area", "STAFF", "SUPERVISOR", "OTHER"];

const Staff: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, loading: authLoading } = useFirebaseAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    role: "",
    status: "",
    search: "",
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Staff | null>(null);
  const [editFormData, setEditFormData] = useState({
    id: "",
    name: "",
    phoneNumber: "",
    role: "",
    otherRole: "",
  });

  // Get unique roles from staff list
  const uniqueRoles = Array.from(new Set(staff.map((s) => s.role))).sort();

  useEffect(() => {
    if (!user || authLoading) return;
    fetchStaff();
  }, [user, authLoading, page, filters]);

  const fetchStaff = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const token = user?.accessToken;

      let staffUrl = `${process.env.REACT_APP_API_HOST}users/company/staff`;
      if (filters.role && filters.role !== "") {
        staffUrl = `${process.env.REACT_APP_API_HOST}users/company/staff/${filters.role}`;
      }

      const staffQueryParams = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(filters.status && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
      });

      const response = await axios.get(`${staffUrl}?${staffQueryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        setStaff(response.data.data || []);
        setTotalPages(response.data.pagination?.pages || 1);
      }
    } catch (error) {
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPage(1);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "ACTIVE":
        return "success";
      case "INACTIVE":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const handleEditClick = (staffMember: Staff) => {
    setSelectedUser(staffMember);
    const isOtherRole = !uniqueRoles.includes(staffMember.role);
    setEditFormData({
      id: staffMember.id,
      name: staffMember.name,
      phoneNumber: staffMember.phoneNumber,
      role: isOtherRole ? "OTHER" : staffMember.role,
      otherRole: isOtherRole ? staffMember.role : "",
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!user || !selectedUser) return;

    setIsLoading(true);
    try {
      const token = user?.accessToken;
      const endpoint = `${process.env.REACT_APP_API_HOST}users/company/staff/update/${selectedUser.id}`;

      const { otherRole, ...payload } = {
        ...editFormData,
        role:
          editFormData.role === "OTHER"
            ? editFormData.otherRole
            : editFormData.role,
      };

      const response = await axios.put(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        showToast("Staff updated successfully", "success");
        setEditModalOpen(false);
        fetchStaff();
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        showToast(
          error.response?.data?.message || "Failed to update staff",
          "error"
        );
      } else {
        showToast("Failed to update staff", "error");
      }
      console.error("Error updating staff:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading || isLoading) {
    return <Loading message="Loading..." fullScreen={true} />;
  }

  const renderStaffCard = (staffMember: Staff) => (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" mb={2}>
          <Avatar
            sx={{
              bgcolor: "primary.main",
              mr: 2,
              width: 56,
              height: 56,
              flexShrink: 0,
            }}
            src={staffMember.profilePhoto || undefined}
          >
            <Person />
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" component="div" noWrap>
              {staffMember.name}
            </Typography>
          </Box>
          <IconButton
            onClick={() => handleEditClick(staffMember)}
            size="small"
            sx={{ ml: 1, flexShrink: 0 }}
          >
            <EditIcon />
          </IconButton>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="body2" color="text.secondary">
            {staffMember.role}
          </Typography>
          <Chip
            label={staffMember.status}
            color={getStatusColor(staffMember.status)}
            size="small"
          />
        </Box>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Phone: {staffMember.phoneNumber || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Joined: {formatDate(staffMember.createdAt)}
            </Typography>
          </Grid>
          {staffMember.lastLoginAt && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Last Login: {formatDate(staffMember.lastLoginAt)}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4" component="h1">
          Staff Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/staff/add/staff")}
        >
          Add Staff
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={filters.role}
                label="Role"
                onChange={(e) => handleFilterChange("role", e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {uniqueRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Staff Grid */}
      <Grid container spacing={3}>
        {staff.map((staffMember) => (
          <Grid item xs={12} sm={6} md={4} key={staffMember.id}>
            {renderStaffCard(staffMember)}
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Staff</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={editFormData.phoneNumber}
                  onChange={(e) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      phoneNumber: e.target.value,
                    }))
                  }
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={editFormData.role}
                    label="Role"
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        role: e.target.value,
                        ...(e.target.value !== "OTHER" && { otherRole: "" }),
                      }))
                    }
                  >
                    {uniqueRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {editFormData.role === "OTHER" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Specify Role"
                    value={editFormData.otherRole}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        otherRole: e.target.value,
                      }))
                    }
                    placeholder="Enter custom role"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={isLoading}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Staff;
